import { FullyDefined } from "src";
import curry from "lodash/curry";
import { CheckboxState } from "react-migration/components/DeprecatedCheckbox";
import { DCSCondition } from "react-migration/lib/typings/comparables/dcs";
import { ZooplaPropertySubType } from "react-migration/lib/typings/Zoopla";
import { EnrichedComparableDTO } from "react-migration/lib/typings/EnrichedComparables";

/**
 * related items do not get rendered but affect the checked status of the item
 * subTypes do get rendered and will affect the checked status of the parent item
 */
export type PropertyTypeOption = DCSCondition & {
  related: FullyDefined<DCSCondition>[];
  i18nCode: string;
  tooltipCode?: string;
  subTypes?: PropertyTypeOption[];
  zooplaRelated?: { propertySubType: ZooplaPropertySubType }[];
};

function selfRelated(
  dcs: FullyDefined<DCSCondition> & {
    i18nCode: string;
    tooltipCode?: string;
    subTypes?: PropertyTypeOption[];
  }
): PropertyTypeOption {
  return {
    ...dcs,
    related: [dcs],
  };
}

export function gatherConditions(current: PropertyTypeOption): DCSCondition[] {
  return [...(current.related ?? []), ...(current.subTypes ?? []).flatMap(gatherConditions)];
}
export function gatherConditionsAndZooplaPropertySubTypes(current: PropertyTypeOption): {
  dcsConditions: DCSCondition[];
  zooplaPropertySubTypes: { propertySubType: ZooplaPropertySubType }[];
}[] {
  return [
    { dcsConditions: current.related, zooplaPropertySubTypes: current.zooplaRelated ?? [] },
    ...(current.subTypes ?? []).flatMap(gatherConditionsAndZooplaPropertySubTypes),
  ];
}

export function gatheredConditionsSelectedState(allConditions: DCSCondition[], selectedConditions: DCSCondition[]) {
  const selected = allConditions.filter((item) => selectedConditions.some((condition) => exactMatch(condition, item)));

  if (selected.length === allConditions.length) {
    return CheckboxState.CHECKED;
  }
  if (!selected.length) {
    return CheckboxState.EMPTY;
  }
  return CheckboxState.INDETERMINATE;
}

export const possibleUKCombinations: PropertyTypeOption[] = [
  {
    designation: "Commercial",
    related: [],
    i18nCode: "comparables.designation.commercial",
    subTypes: [
      {
        designation: "Commercial",
        category: "Industrial",
        i18nCode: "comparables.category.industrial",
        related: [],
        subTypes: [
          selfRelated({
            designation: "Commercial",
            category: "Industrial",
            sub_category: "Detached",
            i18nCode: "comparables.subcategory.detached",
          }),
          selfRelated({
            designation: "Commercial",
            category: "Industrial",
            sub_category: "Flat",
            i18nCode: "comparables.subcategory.flat",
          }),
          selfRelated({
            designation: "Commercial",
            category: "Industrial",
            sub_category: "Manufacturing",
            i18nCode: "comparables.subcategory.manufacturing",
          }),
          selfRelated({
            designation: "Commercial",
            category: "Industrial",
            sub_category: "Other",
            i18nCode: "comparables.subcategory.other",
          }),
          selfRelated({
            designation: "Commercial",
            category: "Industrial",
            sub_category: "Semi_Detached",
            i18nCode: "comparables.subcategory.semi_detached",
          }),
          selfRelated({
            designation: "Commercial",
            category: "Industrial",
            sub_category: "Storage_and_Distribution",
            i18nCode: "comparables.subcategory.storage_and_distribution",
          }),
          selfRelated({
            designation: "Commercial",
            category: "Industrial",
            sub_category: "Terraced",
            i18nCode: "comparables.subcategory.terraced",
          }),
          selfRelated({
            designation: "Commercial",
            category: "Industrial",
            sub_category: "Warehouse",
            i18nCode: "comparables.subcategory.warehouse",
          }),
        ],
      },
      selfRelated({
        designation: "Commercial",
        category: "Land",
        sub_category: "Other",
        i18nCode: "comparables.category.land",
      }),
      {
        designation: "Commercial",
        category: "Office",
        i18nCode: "comparables.category.office",
        related: [],
        subTypes: [
          selfRelated({
            designation: "Commercial",
            category: "Office",
            sub_category: "Detached",
            i18nCode: "comparables.subcategory.detached",
          }),
          selfRelated({
            designation: "Commercial",
            category: "Office",
            sub_category: "Flat",
            i18nCode: "comparables.subcategory.flat",
          }),
          selfRelated({
            designation: "Commercial",
            category: "Office",
            sub_category: "Other",
            i18nCode: "comparables.subcategory.other",
          }),
          selfRelated({
            designation: "Commercial",
            category: "Office",
            sub_category: "Semi_Detached",
            i18nCode: "comparables.subcategory.semi_detached",
          }),
          selfRelated({
            designation: "Commercial",
            category: "Office",
            sub_category: "Terraced",
            i18nCode: "comparables.subcategory.terraced",
          }),
        ],
      },
      {
        designation: "Commercial",
        category: "Other",
        i18nCode: "comparables.category.other",
        tooltipCode: "comparables.category.other.tooltip",
        related: [],
        subTypes: [
          selfRelated({
            designation: "Commercial",
            category: "Other",
            sub_category: "Detached",
            i18nCode: "comparables.subcategory.detached",
          }),
          selfRelated({
            designation: "Commercial",
            category: "Other",
            sub_category: "Flat",
            i18nCode: "comparables.subcategory.flat",
          }),
          selfRelated({
            designation: "Commercial",
            category: "Other",
            sub_category: "Healthcare",
            i18nCode: "comparables.subcategory.healthcare",
          }),
          selfRelated({
            designation: "Commercial",
            category: "Other",
            sub_category: "Hotel",
            i18nCode: "comparables.subcategory.hotel",
          }),
          selfRelated({
            designation: "Commercial",
            category: "Other",
            sub_category: "Leisure",
            i18nCode: "comparables.subcategory.leisure",
          }),
          selfRelated({
            designation: "Commercial",
            category: "Other",
            sub_category: "Other",
            i18nCode: "comparables.subcategory.other",
          }),
          selfRelated({
            designation: "Commercial",
            category: "Other",
            sub_category: "Semi_Detached",
            i18nCode: "comparables.subcategory.semi_detached",
          }),
          selfRelated({
            designation: "Commercial",
            category: "Other",
            sub_category: "Speciality",
            i18nCode: "comparables.subcategory.speciality",
          }),
          selfRelated({
            designation: "Commercial",
            category: "Other",
            sub_category: "Terraced",
            i18nCode: "comparables.subcategory.terraced",
          }),
        ],
      },
      {
        designation: "Commercial",
        category: "Retail",
        i18nCode: "comparables.category.retail",
        related: [],
        subTypes: [
          selfRelated({
            designation: "Commercial",
            category: "Retail",
            sub_category: "Detached",
            i18nCode: "comparables.subcategory.detached",
          }),
          selfRelated({
            designation: "Commercial",
            category: "Retail",
            sub_category: "Flat",
            i18nCode: "comparables.subcategory.flat",
          }),
          selfRelated({
            designation: "Commercial",
            category: "Retail",
            sub_category: "High_Street_Retail",
            i18nCode: "comparables.subcategory.high_street_retail",
          }),
          selfRelated({
            designation: "Commercial",
            category: "Retail",
            sub_category: "Other",
            i18nCode: "comparables.subcategory.other",
          }),
          selfRelated({
            designation: "Commercial",
            category: "Retail",
            sub_category: "Semi_Detached",
            i18nCode: "comparables.subcategory.semi_detached",
          }),
          selfRelated({
            designation: "Commercial",
            category: "Retail",
            sub_category: "Terraced",
            i18nCode: "comparables.subcategory.terraced",
          }),
        ],
      },
    ],
  },

  {
    designation: "Residential",
    i18nCode: "comparables.designation.residential",
    related: [],
    subTypes: [
      {
        designation: "Residential",
        sub_category: "Detached",
        i18nCode: "comparables.subcategory.detached",
        related: [
          { designation: "Residential", category: "House", sub_category: "Detached" },
          { designation: "Residential", category: "Other", sub_category: "Detached" },
          { designation: "Residential", category: "Park_Home", sub_category: "Detached" },
          { designation: "Residential", category: "Maisonette", sub_category: "Detached" },
        ],
        zooplaRelated: [
          { propertySubType: ZooplaPropertySubType.Detached_Property },
          { propertySubType: ZooplaPropertySubType.Detached_House },
        ],
      },
      {
        designation: "Residential",
        sub_category: "Semi_Detached",
        i18nCode: "comparables.subcategory.semi_detached",
        related: [
          { designation: "Residential", category: "House", sub_category: "Semi_Detached" },
          { designation: "Residential", category: "Other", sub_category: "Semi_Detached" },
          { designation: "Residential", category: "Park_Home", sub_category: "Semi_Detached" },
        ],
        zooplaRelated: [
          { propertySubType: ZooplaPropertySubType.Semi_Detached_Property },
          { propertySubType: ZooplaPropertySubType.Semi_Detached_House },
        ],
      },
      {
        designation: "Residential",
        sub_category: "Terraced",
        i18nCode: "comparables.subcategory.terraced",
        related: [
          { designation: "Residential", category: "House", sub_category: "Terraced" },
          { designation: "Residential", category: "Other", sub_category: "Terraced" },
          { designation: "Residential", category: "Park_Home", sub_category: "Terraced" },
        ],
        zooplaRelated: [
          { propertySubType: ZooplaPropertySubType.End_Terrace_Property },
          { propertySubType: ZooplaPropertySubType.End_Terrace_House },
          { propertySubType: ZooplaPropertySubType.Mid_Terrace_Property },
          { propertySubType: ZooplaPropertySubType.Mid_Terrace_House },
          { propertySubType: ZooplaPropertySubType.Terrace_Property },
          { propertySubType: ZooplaPropertySubType.Terraced },
          { propertySubType: ZooplaPropertySubType.Town_House },
        ],
      },
      {
        designation: "Residential",
        category: "Bungalow",
        i18nCode: "comparables.category.bungalow",
        related: [
          { designation: "Residential", category: "Bungalow", sub_category: "Detached" },
          { designation: "Residential", category: "Bungalow", sub_category: "Semi_Detached" },
          { designation: "Residential", category: "Bungalow", sub_category: "Terraced" },
          { designation: "Residential", category: "Bungalow", sub_category: "Other" },
        ],
        zooplaRelated: [
          { propertySubType: ZooplaPropertySubType.End_Terrace_Bungalow },
          { propertySubType: ZooplaPropertySubType.Mid_Terrace_Bungalow },
          { propertySubType: ZooplaPropertySubType.Terraced_Bungalow },
          { propertySubType: ZooplaPropertySubType.Semi_Detached_Bungalow },
          { propertySubType: ZooplaPropertySubType.Bungalow_Property },
          { propertySubType: ZooplaPropertySubType.Detached_Bungalow },
        ],
      },
      {
        designation: "Residential",
        category: "Maisonette",
        // the only reason we had to add the i18nCode value for EVERYTHING was for this one entry which doesn't fit the previous model
        i18nCode: "comparables.category.maisonette_or_flat",
        related: [],
        subTypes: [
          {
            designation: "Residential",
            category: "Maisonette",
            i18nCode: "comparables.category.maisonette",
            related: [
              { designation: "Residential", category: "Maisonette", sub_category: "Flat" },
              { designation: "Residential", category: "Maisonette", sub_category: "Other" },
              { designation: "Residential", category: "Maisonette", sub_category: "Semi_Detached" },
              { designation: "Residential", category: "Maisonette", sub_category: "Terraced" },
            ],
            zooplaRelated: [
              { propertySubType: ZooplaPropertySubType.Converted_Flat },
              { propertySubType: ZooplaPropertySubType.Purpose_Built_Flat },
              { propertySubType: ZooplaPropertySubType.Retirement_Flat },
            ],
          },
          {
            designation: "Residential",
            category: "Flat",
            i18nCode: "comparables.category.flat",
            related: [
              { designation: "Residential", category: "Park_Home", sub_category: "Flat" },
              { designation: "Residential", category: "House", sub_category: "Flat" },
              { designation: "Residential", category: "Bungalow", sub_category: "Flat" },
            ],
            zooplaRelated: [{ propertySubType: ZooplaPropertySubType.Flat_Maisonette }],
          },
        ],
      },
      {
        designation: "Residential",
        category: "Other",
        i18nCode: "comparables.category.other",
        tooltipCode: "comparables.category.other.tooltip",
        related: [
          { designation: "Residential", category: "House", sub_category: "Other" },
          { designation: "Residential", category: "Other", sub_category: "Other" },
          { designation: "Residential", category: "Park_Home", sub_category: "Other" },
        ],
        zooplaRelated: [
          { propertySubType: ZooplaPropertySubType.Property },
          { propertySubType: ZooplaPropertySubType.House },
        ],
      },
    ],
  },
];

export const possibleHometrackCombinations: PropertyTypeOption[] = [
  {
    designation: "Residential",
    category: "Detached",
    i18nCode: "comparables.subcategory.detached",
    related: [
      // Detached Bungalow, Detached House, Detached Property
      { designation: "Residential", sub_category: "Detached Property", category: "Detached" },
      { designation: "Residential", sub_category: "Detached House", category: "Detached" },
      { designation: "Residential", sub_category: "Detached Bungalow", category: "Detached" },
    ],
  },
  {
    designation: "Residential",
    category: "Semi-Detached",
    i18nCode: "comparables.subcategory.semi_detached",
    related: [
      {
        designation: "Residential",
        sub_category: "Semi-Detached Property",
        category: "Semi-Detached",
      },
      {
        designation: "Residential",
        sub_category: "Semi-Detached House",
        category: "Semi-Detached",
      },
      {
        designation: "Residential",
        sub_category: "Semi-Detached Bungalow",
        category: "Semi-Detached",
      },
    ],
  },
  {
    designation: "Residential",
    category: "Terraced",
    i18nCode: "comparables.subcategory.terraced",
    related: [
      {
        designation: "Residential",
        sub_category: "End Terrace Bungalow",
        category: "Terraced",
      },
      { designation: "Residential", sub_category: "End Terrace House", category: "Terraced" },
      {
        designation: "Residential",
        sub_category: "End Terrace Property",
        category: "Terraced",
      },
      {
        designation: "Residential",
        sub_category: "Mid Terrace Property",
        category: "Terraced",
      },
      {
        designation: "Residential",
        sub_category: "Mid Terrace Bungalow",
        category: "Terraced",
      },
      { designation: "Residential", sub_category: "Mid Terrace House", category: "Terraced" },
      { designation: "Residential", sub_category: "Terrace Property", category: "Terraced" },
      { designation: "Residential", sub_category: "Terraced", category: "Terraced" },
      { designation: "Residential", sub_category: "Terraced Bungalow", category: "Terraced" },
      { designation: "Residential", sub_category: "Town House", category: "Terraced" },
    ],
  },
  {
    designation: "Residential",
    category: "Flat",
    // the only reason we had to add the i18nCode value for EVERYTHING was for this one entry which doesn't fit the previous model
    i18nCode: "comparables.category.flat",
    related: [
      { designation: "Residential", sub_category: "Converted Flat", category: "Flat" },
      { designation: "Residential", sub_category: "Purpose Built Flat", category: "Flat" },
      { designation: "Residential", sub_category: "Retirement Flat", category: "Flat" },
      { designation: "Residential", sub_category: "Flat/Maisonette", category: "Flat" },
    ],
  },
  {
    designation: "Residential",
    category: "Bungalow",
    // the only reason we had to add the i18nCode value for EVERYTHING was for this one entry which doesn't fit the previous model
    i18nCode: "comparables.category.bungalow",
    related: [
      { designation: "Residential", sub_category: "Bungalow Property", category: "Bungalow" },
      { designation: "Residential", sub_category: "Detached Bungalow", category: "Bungalow" },
      { designation: "Residential", sub_category: "Semi-Detached Bungalow", category: "Bungalow" },
      { designation: "Residential", sub_category: "Terraced Bungalow", category: "Bungalow" },
      {
        designation: "Residential",
        sub_category: "End Terrace Bungalow",
        category: "Bungalow",
      },
      {
        designation: "Residential",
        sub_category: "Mid Terrace Bungalow",
        category: "Bungalow",
      },
    ],
  },
];

export const possibleUSCombinations: PropertyTypeOption[] = [
  {
    designation: "Commercial",
    i18nCode: "comparables.designation.commercial",
    related: [],
  },
  selfRelated({
    designation: "Residential",
    category: "Other",
    sub_category: "Other",
    i18nCode: "comparables.designation.residential",
    tooltipCode: "comparables.category.other.tooltip",
  }),
  selfRelated({
    designation: "Other",
    category: "Other",
    sub_category: "Other",
    i18nCode: "comparables.designation.other",
    tooltipCode: "comparables.category.other.tooltip",
    subTypes: [
      selfRelated({
        designation: "Commercial",
        category: "Agriculture",
        sub_category: "Other",
        i18nCode: "comparables.category.agriculture",
      }),
      selfRelated({
        designation: "Commercial",
        category: "Industrial",
        sub_category: "Other",
        i18nCode: "comparables.category.industrial",
      }),
      selfRelated({
        designation: "Commercial",
        category: "Land",
        sub_category: "Other",
        i18nCode: "comparables.category.land",
      }),
      selfRelated({
        designation: "Commercial",
        category: "Other",
        sub_category: "Other",
        i18nCode: "comparables.category.other",
        tooltipCode: "comparables.category.other.tooltip",
      }),
    ],
  }),
];

export function rejectInvalidCombinations(allowedOptions: DCSCondition[], options: DCSCondition[]) {
  return options.filter((option) => allowedOptions.some(curry(exactMatch, 2)(option)));
}

/**
 * matches exactly such that any key defined in either object must exist and match in the other object
 * @param a
 * @param b
 * @returns
 */
export function exactMatch(a: DCSCondition, b: DCSCondition) {
  return a.designation === b.designation && a.category === b.category && a.sub_category === b.sub_category;
}

/**
 * matches loosely such that undefined keys in either object are not used to match
 * @param a
 * @param b
 * @returns
 */
export function looseMatch(a: DCSCondition, b: DCSCondition) {
  const keys: Array<keyof DCSCondition> = ["designation", "category", "sub_category"];
  return keys.every((key) => {
    return (a[key] || b[key]) === (b[key] || a[key]);
  });
}

// Helper to format snake_case to Title Case
const formatDisplayName = (str: string): string => {
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * Formats property type strings according to the hierarchical display requirements
 * @param transaction - The enriched comparable transaction
 * @param accessLevel - User's Zoopla access level
 * @param isUSUser - Whether the user is in the US
 * @returns Formatted property type string or null
 */
export const getFormattedPropertyType = (
  transaction: EnrichedComparableDTO,
  accessLevel: "none" | "light" | "full",
  isUSUser: boolean
): string | null => {
  const { designation, category, sub_category } = transaction;

  if (designation === "Commercial") {
    const residentialSubCategories = ["Detached", "Semi_Detached", "Terraced", "Flat"];

    // If sub_category is a residential type display the main category if available
    if (sub_category && residentialSubCategories.includes(sub_category)) {
      return category || null;
    }

    const combinations = isUSUser ? possibleUSCombinations : possibleUKCombinations;

    for (const option of combinations) {
      if (option.subTypes) {
        for (const subType of option.subTypes) {
          if (subType.subTypes) {
            for (const leafType of subType.subTypes) {
              if (
                leafType.related.some(
                  (related) => related.category === category && related.sub_category === sub_category
                )
              ) {
                const displaySubCategory = formatDisplayName(sub_category!);

                if (sub_category === "Other") {
                  return category || null;
                }
                return `${category} - ${displaySubCategory}`;
              }
            }
          } else if (
            subType.related.some((related) => related.category === category && related.sub_category === sub_category)
          ) {
            return category || null;
          }
        }
      }
    }

    return category || null;
  } else {
    const zooplaSubType = transaction.zooplaEnrichment?.propertySubType;
    const zooplaPropertyType = transaction.zooplaEnrichment?.propertyType;
    const hasZooplaData = zooplaSubType || zooplaPropertyType;

    if (accessLevel === "full" && hasZooplaData) {
      const option = possibleHometrackCombinations.find((opt) =>
        opt.related.some((related) => related.sub_category === zooplaSubType || related.category === zooplaPropertyType)
      );
      return option?.category || null;
    }

    const combinations = isUSUser ? possibleUSCombinations : possibleUKCombinations;

    const residentialOption = combinations.find((opt) => opt.designation === "Residential");
    if (residentialOption?.subTypes) {
      for (const subType of residentialOption.subTypes) {
        if (subType.subTypes) {
          for (const leafType of subType.subTypes) {
            if (
              leafType.related.some((related) => related.category === category && related.sub_category === sub_category)
            ) {
              if (subType.category === "Maisonette" && category === "Flat") {
                return "Maisonette - Flat";
              }
              return `${category} - ${formatDisplayName(sub_category!)}`;
            }
          }
        } else if (
          subType.related.some((related) => related.category === category && related.sub_category === sub_category)
        ) {
          return formatDisplayName(sub_category!);
        }
      }
    }

    return null;
  }
};
